<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_FIXED_ASSET')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
          <b-dropdown
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            class="ml-1"
            :text="$t('Export')"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <b-table
      ref="branchRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="fixedAssetRegisterList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateOfAcquisition)="data">
        <p class="mb-0">
          {{ formatDate(data.value ) }}
        </p>
      </template>
      <template #cell(dateOfOperation)="data">
        <p class="mb-0">
          {{ formatDate(data.value ) }}
        </p>
      </template>
      <template #cell(retirementDate)="data">
        <p class="mb-0">
          {{ formatDate(data.value ) }}
        </p>
      </template>
      <template #cell(originalBookValue)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>
      <template #cell(monthlyDepreciationValue)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>
      <template #cell(accumulatedDepreciationValue)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>
      <template #cell(netAssetValue)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>
      <template #cell(salvageValue)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>

      <template #cell(dateCreated)="data">
        {{ formatDate(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template
        #cell(actions)="data"
      >
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_FIXED_ASSET') && data.item.status !== 'Liquidated'"
              @click="onSelectFixedAssetItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="onSelectFixedAssetItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_FIXED_ASSET' : 'ACTIVATE_FIXED_ASSET')"
              @click="fixedAssetStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="fixedAssetRegisterRules">
        <b-form>
          <b-row class="match-height">
            <!-- Name -->
            <b-col md="6">
              <InputField
                id="asset-name"
                v-model="fixedAssetRegisterFormData.name"
                :label="$t('Asset Name')"
                :placeholder="$t('Enter asset name')"
                type="text"
                :error-name="$t('Asset Name')"
                rules="required"
                for="asset-name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Name -->
            <!-- Description -->
            <b-col md="6">
              <InputField
                id="description"
                v-model="fixedAssetRegisterFormData.description"
                :label="$t('Asset Description')"
                :placeholder="$t('Enter Asset Description')"
                type="text"
                :error-name="$t('Asset Description')"
                rules="required"
                for="description"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Description -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="fixedAssetRegisterFormData.branch"
                :reduce="option => option.value"
                :label="$t('Branch of use')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                rules="required"
                :error-name="$t('Branch of use')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Branch -->
            <!-- Fixed Asset Category -->
            <b-col
              md="6"
            >
              <SelectField
                id="fixed-asset-category"
                v-model="fixedAssetRegisterFormData.category"
                :label="$t('Fixed Asset Category')"
                :placeholder="$t('Select')"
                :options="translatedFixedAssetCategoryOptions"
                :reduce="option => option.value"
                :error-name="$t('Fixed Asset Category')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Fixed Asset Catgeory -->
            <!-- Location -->
            <b-col md="6">
              <InputField
                id="location"
                v-model="fixedAssetRegisterFormData.location"
                :label="$t('Actual Location')"
                :placeholder="$t('Enter Location')"
                type="text"
                :error-name="$t('Actual Location')"
                rules="required"
                for="location"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Location -->
            <!-- Serial Number -->
            <b-col md="6">
              <InputField
                id="serial-number"
                v-model="fixedAssetRegisterFormData.serialNumber"
                :label="$t('Serial Number')"
                :placeholder="$t('Enter Serial Number')"
                type="text"
                :error-name="$t('Serial Number')"
                rules="required"
                for="serial-number"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Serial Number -->
            <!-- Registration Number -->
            <b-col md="6">
              <InputField
                id="registration-number"
                v-model="fixedAssetRegisterFormData.registrationNumber"
                :label="$t('Registration/Tag Number')"
                :placeholder="$t('Enter registration / tag number')"
                type="text"
                :error-name="$t('Registration/Tag Number')"
                rules="required"
                for="registration-number"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Registration Number -->
            <!-- Date of Acquisition -->
            <b-col md="6">
              <DatePickerField
                id="date-of-acquisition"
                v-model="fixedAssetRegisterFormData.dateOfAcquisition"
                rules="required"
                :min-date="new Date()"
                :label="$t('Date of Acquisition')"
                :error-name="$t('Date of Acquisition')"
                :config="datePickerConfig"
                :placeholder="$t('Date of Acquisition')"
                :disabled="isReadonly"
                vid="maxDate"
              />
            </b-col>
            <!-- Date of Acquisition -->
            <!-- Date Asset was put to Operation -->
            <b-col md="6">
              <DatePickerField
                id="date-of-operation"
                v-model="fixedAssetRegisterFormData.dateOfOperation"
                rules="required|isGreaterThanDate:@maxDate"
                :label="$t('Date Asset was put to Operation')"
                :error-name="$t('Date Asset was put to Operation')"
                :config="datePickerConfig"
                :placeholder="$t('Date Asset was put to Operation')"
                :disabled="isReadonly"
              />
            </b-col>
            <!-- Date Asset was put to Operation -->
            <!-- Date of Retirement -->
            <b-col
              v-if="isViewOnly"
              md="6"
            >
              <InputField
                id="date-of-retirement"
                v-model="fixedAssetRegisterFormData.retirementDate"
                :label="$t('Date of Retirement')"
                :placeholder="$t('Date of Retirement')"
                type="text"
                readonly
              />
            </b-col>
            <!-- Date of Retirement -->
            <b-col md="6">
              <h6 class="mt-0 mb-0">
                {{ $t('Existence') }}
              </h6>
              <b-form-radio-group
                v-model="fixedAssetRegisterFormData.existence"
                :options="existenceOptions"
                class="demo-inline-spacing"
                name="radios-stacked"
                stacked
                value-field="value"
                text-field="text"
                disabled-field="disabled"
                :disabled="isEditable"
                @input="setExistence(fixedAssetRegisterFormData.existence)"
              />
            </b-col>
            <!-- Payment Source -->
            <b-col
              v-if="isExisting"
              md="6"
            >
              <SelectField
                id="payment-source-general-ledger"
                v-model="fixedAssetRegisterFormData.paymentSourceGeneralLedger"
                :label="$t('Payment Source')"
                :placeholder="$t('Select')"
                :options="paymentSourceGeneralLedgerOptions"
                :reduce="option => option.value"
                :error-name="$t('Payment Source')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Original Book Value -->
            <b-col md="6">
              <InputField
                id="original-book-value"
                v-model="fixedAssetRegisterFormData.originalBookValue"
                :label="$t('Original Book Value')"
                :placeholder="$t('Enter Original Book Value')"
                type="number"
                :error-name="$t('Original Book Value')"
                rules="required|limited2Decimal"
                for="original-book-value"
                :readonly="isReadonly"
                @input="computeDepreciation"
              />
            </b-col>
            <!-- Original Book Value -->
            <!-- Salvage Value -->
            <b-col md="6">
              <InputField
                id="salvage-value"
                v-model="fixedAssetRegisterFormData.salvageValue"
                :label="$t('Salvage Value')"
                :placeholder="$t('Enter Salvage Value')"
                type="number"
                :error-name="$t('Salvage Value')"
                rules="required|limited2Decimal"
                for="salvage-value"
                :readonly="isReadonly"
                @input="computeDepreciation"
              />
            </b-col>
            <!-- Salvage Value -->
            <!-- Useful Life (Total In months) -->
            <b-col md="6">
              <InputField
                id="usefulLife-value"
                v-model="fixedAssetRegisterFormData.usefulLife"
                :label="$t('Useful Life (Total In months)')"
                :placeholder="$t('Enter Useful Life')"
                type="number"
                :error-name="$t('Useful Life')"
                rules="required|integer"
                for="usefulLife-value"
                :readonly="isReadonly"
                @input="computeDepreciation"
              />
            </b-col>
            <!-- Useful Life (Total In months) -->
            <!-- Monthly Depr. Value -->
            <b-col md="6">
              <InputField
                id="monthly-depreciation-value"
                v-model="fixedAssetRegisterFormData.monthlyDepreciationValue"
                :label="$t('Monthly Depr. Value')"
                :placeholder="$t('Enter Monthly Depr. Value')"
                type="number"
                :error-name="$t('Monthly Depr. Value')"
                rules="required|upTo2Decimal"
                for="monthly-depreciation-value"
                readonly
              />
            </b-col>
            <!-- Monthly Depr. Value -->

            <!-- Monthly Depr. Rate(%) -->
            <b-col md="6">
              <InputField
                id="monthly-depreciation-rate"
                v-model="fixedAssetRegisterFormData.monthlyDepreciationRate"
                :label="$t('Monthly Depr. Rate(%)')"
                :placeholder="$t('Enter Monthly Depr. Rate(%)')"
                type="number"
                :error-name="$t('Monthly Depr. Rate(%)')"
                rules="required|upTo3Decimal"
                for="monthly-depreciation-rate"
                readonly
              />
            </b-col>
            <!-- Monthly Depr. Rate(%) -->
            <!-- Annual Depr. Value -->
            <b-col md="6">
              <InputField
                id="annual-depreciation-value"
                v-model="fixedAssetRegisterFormData.annualDepreciationValue"
                :label="$t('Annual Depr. Value')"
                :placeholder="$t('Enter Annual Depr. Value')"
                type="number"
                :error-name="$t('Annual Depr. Value')"
                rules="required|upTo2Decimal"
                for="annual-depreciation-value"
                readonly
              />
            </b-col>
            <!-- Annual Depr. Value -->

            <!-- Annual Depr. Rate(%) -->
            <b-col md="6">
              <InputField
                id="annual-depreciation-rate"
                v-model="fixedAssetRegisterFormData.annualDepreciationRate"
                :label="$t('Annual Depr. Rate(%)')"
                :placeholder="$t('Enter Annual Depr. Rate(%)')"
                type="number"
                :error-name="$t('Annual Depr. Rate(%)')"
                rules="required|upTo3Decimal"
                for="annual-depreciation-rate"
                readonly
              />
            </b-col>
            <!-- Annual Depr. Rate(%) -->
            <!-- Accum. Depr. Value -->
            <b-col
              v-if="isNotExisting"
              md="6"
            >
              <InputField
                id="accumulated-depreciation-value"
                v-model="fixedAssetRegisterFormData.accumulatedDepreciationValue"
                :label="$t('Accum. Depr. Value')"
                :placeholder="$t('Enter Accum. Depr. Value')"
                type="number"
                :error-name="$t('Accum. Depr. Value')"
                rules="required|limited2Decimal"
                for="accumulated-depreciation-value"
                :readonly="isReadonly"
                @input="computeDepreciation"
              />
            </b-col>
            <!-- Accum. Depr. Value -->
            <!-- Net Asset Value-->
            <b-col md="6">
              <InputField
                id="net-asset-value"
                v-model="fixedAssetRegisterFormData.netAssetValue"
                :label="$t('Net Asset Value')"
                :placeholder="$t('Enter Net Asset Value')"
                type="number"
                :error-name="$t('Net Asset Value')"
                rules="required"
                for="net-asset-value"
                readonly
              />
            </b-col>
            <!-- Net Asset Value-->
            <!-- Fixed Asset GL -->
            <b-col
              md="6"
            >
              <SelectField
                id="fixed-asset-general-ledger"
                v-model="fixedAssetRegisterFormData.fixedAssetGeneralLedger"
                :label="$t('Fixed Asset GL')"
                :placeholder="$t('Select')"
                :options="fixedAssetGeneralLedgerOptions"
                :reduce="option => option.value"
                :error-name="$t('Fixed Asset GL')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Fixed Asset GL -->

            <b-col
              md="6"
            >
              <SelectField
                id="accumulated-depreciation-general-ledger"
                v-model="fixedAssetRegisterFormData.accumulatedDepreciationGeneralLedger"
                :label="$t('Accumulated depreciation GL')"
                :placeholder="$t('Select')"
                :options="accumulatedDepreciationGeneralLedgerOptions"
                :reduce="option => option.value"
                :error-name="$t('Accumulated depreciation GL')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>

            <b-col
              md="6"
            >
              <SelectField
                id="depreciation-expense-general-ledger"
                v-model="fixedAssetRegisterFormData.depreciationExpenseGeneralLedger"
                :label="$t('Depreciation Expense GL')"
                :placeholder="$t('Select')"
                :options="depreciationExpenseGeneralLedgerOptions"
                :reduce="option => option.value"
                :error-name="$t('Depreciation Expense GL')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col
              md="6"
            >
              <SelectField
                id="fixed-asset-disposal-general-ledger"
                v-model="fixedAssetRegisterFormData.fixedAssetDisposalGeneralLedger"
                :label="$t('Fixed Asset Disposal GL')"
                :placeholder="$t('Select')"
                :options="fixedAssetDisposalGeneralLedgerOptions"
                :reduce="option => option.value"
                :error-name="$t('Fixed Asset Disposal GL')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row>

        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div class="text-center mt-1 mx-auto">
          <b-row>
            <b-col cols="">
              <Button
                :label="$t('Generate QR Code')"
                variant="primary"
                size="md"
                block
                @click="generateQRCode"
              />
            </b-col>
            <b-col cols="">
              <Button
                v-if="getUserPermission('LIQUIDATE_FIXED_ASSET')"
                :label="$t('Liquidate Asset')"
                :loading="isLoading"
                :disabled="isDisabled"
                variant="primary"
                size="md"
                @click="handleLiquidateAsset"
              />
            </b-col>
          </b-row>
        </div>
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchFixedAssetRegister"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Asset Name -->
        <b-col md="6">
          <InputField
            id="asset-name"
            v-model="filter.name"
            :label="$t('Asset Name')"
            :placeholder="$t('Enter asset name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Asset Name -->

        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branch"
            :reduce="option => option.value"
            :label="$t('Branch')"
            :placeholder="$t('Select')"
            :options="branchOptions"
            item="label"
          />
        </b-col>
        <!-- Branch -->
        <!-- Fixed Asset Category -->
        <b-col
          md="6"
        >
          <SelectField
            id="fixed-asset-category"
            v-model="filter.type"
            :label="$t('Asset Category')"
            :placeholder="$t('Select')"
            :options="translatedFixedAssetCategoryOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Fixed Asset Catgeory -->
        <!-- Registration Number -->
        <b-col md="6">
          <InputField
            id="registration-number"
            v-model="filter.registrationNumber"
            :label="$t('Registration/Tag Number')"
            :placeholder="$t('Enter registration / tag number')"
            type="text"
            for="registration-number"
          />
        </b-col>
        <!-- Registration Number -->

        <!-- Date of Acquisition From -->
        <b-col md="6">
          <DatePickerField
            id="date-of-acquisition-from"
            v-model="filter.startDate"
            :min-date="new Date()"
            :label="$t('Acquisition Date From')"
            :config="datePickerConfig"
            :placeholder="$t('Acquisition Date From')"
          />
        </b-col>
        <!-- Date of Acquisition From -->
        <!-- Date of Acquisition To -->
        <b-col md="6">
          <DatePickerField
            id="date-of-acquisition-to"
            v-model="filter.endDate"
            :min-date="new Date()"
            :label="$t('Acquisition Date To')"
            :config="endDatePickerConfig"
            :placeholder="$t('Acquisition Date To')"
          />
        </b-col>
        <!-- Date of Acquisition To -->
        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option.value"
            :placeholder="$t('Select')"
            :options="translatedStatusOptions"
            item="label"
          />
        </b-col>
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <!-- QR Code modal -->
    <b-modal
      id="modal-qrCode"
      ref="qr-modal"
      :ok-title="$t('Download QR Code')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="sm"
      :title="$t('Fixed Asset Generated QR Code')"
      modal-footer
      @ok="downloadQRCode"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="12">
          <div
            ref="printcontent"
            class="p-2"
          >
            <qrcode-vue
              :value="qrCodePrintableValue"
              :size="size"
              level="H"
            />
          </div>
        </b-col>
        <!-- Name -->
      </b-row>
    </b-modal>
    <!-- QR Code modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BBadge, BForm, BDropdownItem, BDropdown, BFormRadioGroup,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { toastAlert } from '@core/mixins/toast'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapGetters, mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import QrcodeVue from 'qrcode.vue'
import html2canvas from 'html2canvas'
import moment from 'moment'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toDateWithYear, toDateWithYearFirst } from '@/helpers/formatDate'
import {
  toAmount, toKobo, toBalance, formatAmount,
} from '@/helpers/formatAmount'
import DatePickerField from '../../forms/DatePickerField.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import getStatusColor from '../../../helpers/getStatusColor'
import Button from '../../components/button/Button.vue'
import { accountPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BModal,
    BForm,
    BBadge,
    BSpinner,
    SelectField,
    TablePagination,
    TableEntries,
    ValidationObserver,
    InputField,
    BDropdownItem,
    BDropdown,
    FullScreenSpinner,
    Button,
    DatePickerField,
    BFormRadioGroup,
    QrcodeVue,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin, toastAlert],
  data() {
    return {
      toAmount,
      toBalance,
      formatAmount,
      qrCodePrintableValue: '',
      size: 300,
      isExisting: false,
      isNotExisting: true,
      isViewOnly: false,
      formatDate: toDateWithYear,
      toDateWithYearFirst,
      isLoading: false,
      isReadonly: false,
      isEditable: false,
      isDisabled: false,
      buttonLabel: this.$t('Save'),
      sortBy: 'name',
      required,
      modalTitle: this.$t('Create Fixed Assets Register'),
      sortDesc: false,
      showSpinner: false,
      getStatusColor,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Asset Name' },
        { key: 'branchName', sortable: true, label: 'Branch Name' },
        { key: 'dateOfAcquisition', sortable: true, label: 'Acquisition Date' },
        { key: 'dateOfOperation', sortable: true, label: 'Operation Date' },
        { key: 'usefulLife', sortable: true, label: 'Useful Life(In Months)' },
        { key: 'retirementDate', sortable: true, label: 'Retirement Date' },
        { key: 'category', sortable: true, label: 'Asset Category' },
        { key: 'registrationNumber', sortable: true, label: 'Reg. No' },
        { key: 'originalBookValue', sortable: true, label: 'Original Book Value' },
        { key: 'annualDepreciationRate', sortable: true, label: 'Annual Dep. (%)' },
        { key: 'monthlyDepreciationValue', sortable: true, label: 'Monthly Dep. Value' },
        { key: 'accumulatedDepreciationValue', sortable: true, label: 'Accum. Dep. Value' },
        { key: 'netAssetValue', sortable: true, label: 'Net Asset Value' },
        { key: 'salvageValue', sortable: true, label: 'Salvage Value' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      fixedAssetRegisterList: [],
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
        ariaDateFormat: 'Y-m-d',
      },
      fixedAssetRegisterFormData: {
        name: '',
        description: '',
        branch: '',
        branchName: '',
        category: '',
        location: '',
        serialNumber: '',
        registrationNumber: '',
        dateOfAcquisition: '',
        dateOfOperation: '',
        retirementDate: '',
        existence: 'AlreadyExisting',
        originalBookValue: '',
        salvageValue: '',
        usefulLife: '',
        monthlyDepreciationValue: '',
        monthlyDepreciationRate: '',
        annualDepreciationValue: '',
        annualDepreciationRate: '',
        accumulatedDepreciationValue: 0,
        netAssetValue: '',
        paymentSourceGeneralLedger: '',
        fixedAssetGeneralLedger: '',
        accumulatedDepreciationGeneralLedger: '',
        depreciationExpenseGeneralLedger: '',
        fixedAssetDisposalGeneralLedger: '',
        id: '',
      },
      existenceOptions: [
        { value: 'AlreadyExisting', text: this.$t('Already Existing') },
        { value: 'NotExisting', text: this.$t('Not Existing') },
      ],
      fixedAssetCategoryOptions: [
        { label: 'Freehold/Leasehold Land', value: 'Freehold/Leasehold Land' },
        { label: 'Furniture And Fixtures', value: 'Furniture And Fixtures' },
        { label: 'Plant And Machinery', value: 'Plant And Machinery' },
        { label: 'Freehold/Leasehold Building', value: 'Freehold/Leasehold Building' },
        { label: 'Motor Vehicles', value: 'Motor Vehicles' },
        { label: 'Office Equipment', value: 'Office Equipment' },
        { label: 'Computer Softwares', value: 'Computer Softwares' },
        { label: 'Intangible Assets', value: 'Intangible Assets' },
      ],
      accumulatedDepreciationGeneralLedgerOptions: [],
      paymentSourceGeneralLedgerOptions: [],
      depreciationExpenseGeneralLedgerOptions: [],
      fixedAssetGeneralLedgerOptions: [],
      fixedAssetDisposalGeneralLedgerOptions: [],
      permission: '',
      filter: {
        name: '',
        branch: '',
        startDate: '',
        endDate: '',
        registrationNumber: '',
        status: '',
        sortColumn: '',
      },
      statusOptions: [
        { label: 'Liquidated', value: 'Liquidated' },
        { label: 'Active', value: 'Active' },
        { label: 'InActive', value: 'InActive' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedFixedAssetCategoryOptions() {
      return this.fixedAssetCategoryOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedStatusOptions() {
      return this.statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    endDatePickerConfig() {
      return {
        ...this.datePickerConfig,
        minDate: this.filter.startDate ? new Date(this.filter.startDate) : new Date(),
      }
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchFixedAssetRegister()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchFixedAssetRegister()
    },
  },
  mounted() {
    this.getPermission()
    this.fetchFixedAssetRegister()
    this.getPaymentSourceGL()
    this.getfixedAssetParentGL()
    this.getfixedAssetDepreciationExpenseGL()
    this.getfixedAssetAccumulatedDepreciationGL()
    this.getGLAccountsByAccountTypes()
  },

  methods: {
    ...mapActions({
      getGeneralLedger: 'account/getGeneralLedgerList',
      getfixedAssetParentGeneralLedger: 'account/getfixedAssetParentGeneralLedger',
      getpaymentSourceGeneralLedger: 'account/getpaymentSourceGeneralLedger',
      getFixedAssetRegister: 'account/getFixedAssetRegisterList',
      updateFixedAssetRegister: 'account/updateFixedAssetRegisterAction',
      createFixedAssetRegister: 'account/createFixedAssetRegisterAction',
      updateFixedAssetStatus: 'account/updateStatus',
      liquidateAssetRegister: 'account/liquidateAssetRegister',
      getGLAccountsByAccountType: 'account/getGLAccountsByAccountTypeList',
      downloadFixedAssetRegisterReport: 'reports/downloadFixedAssetRegisterReport',
    }),
    getPermission() {
      if (this.actionType === 'save' || this.actionType === 'add') {
        this.permission = accountPermissions.CREATE_FIXED_ASSET
        return
      }
      if (this.actionType === 'edit') {
        this.permission = accountPermissions.MODIFY_FIXED_ASSET
        return
      }
      this.permission = accountPermissions.VIEW_FIXED_ASSET
    },
    setExistence(val) {
      if (val === 'AlreadyExisting') {
        this.isExisting = false
        this.isNotExisting = true
      } else {
        this.isExisting = true
        this.isNotExisting = false
        this.fixedAssetRegisterFormData.accumulatedDepreciationValue = 0
      }
    },
    async onSelectFixedAssetItem(items, activity, val) {
      if (val === 'view') {
        this.isViewOnly = true
      }
      this.isEditable = true
      this.fixedAssetRegisterFormData.name = items.name
      this.fixedAssetRegisterFormData.description = items.description
      this.fixedAssetRegisterFormData.branch = items.branch
      this.fixedAssetRegisterFormData.branchName = items.branchName
      this.fixedAssetRegisterFormData.category = items.category
      this.fixedAssetRegisterFormData.location = items.location
      this.fixedAssetRegisterFormData.serialNumber = items.serialNumber
      this.fixedAssetRegisterFormData.registrationNumber = items.registrationNumber
      this.fixedAssetRegisterFormData.dateOfAcquisition = items.dateOfAcquisition
      this.fixedAssetRegisterFormData.dateOfOperation = items.dateOfOperation
      this.fixedAssetRegisterFormData.retirementDate = items.retirementDate
      this.fixedAssetRegisterFormData.existence = items.existence
      this.fixedAssetRegisterFormData.paymentSourceGeneralLedger = items.paymentSourceGeneralLedger
      this.fixedAssetRegisterFormData.originalBookValue = formatAmount(items.originalBookValue)
      this.fixedAssetRegisterFormData.salvageValue = formatAmount(items.salvageValue)
      this.fixedAssetRegisterFormData.usefulLife = items.usefulLife
      this.fixedAssetRegisterFormData.monthlyDepreciationValue = formatAmount(items.monthlyDepreciationValue)
      this.fixedAssetRegisterFormData.monthlyDepreciationRate = items.monthlyDepreciationRate
      this.fixedAssetRegisterFormData.annualDepreciationValue = formatAmount(items.annualDepreciationValue)
      this.fixedAssetRegisterFormData.annualDepreciationRate = items.annualDepreciationRate
      this.fixedAssetRegisterFormData.accumulatedDepreciationValue = formatAmount(items.accumulatedDepreciationValue)
      this.fixedAssetRegisterFormData.netAssetValue = formatAmount(items.netAssetValue)
      this.fixedAssetRegisterFormData.fixedAssetGeneralLedger = items.fixedAssetGeneralLedger
      this.fixedAssetRegisterFormData.accumulatedDepreciationGeneralLedger = items.accumulatedDepreciationGeneralLedger
      this.fixedAssetRegisterFormData.depreciationExpenseGeneralLedger = items.depreciationExpenseGeneralLedger
      this.fixedAssetRegisterFormData.fixedAssetDisposalGeneralLedger = items.fixedAssetDisposalGeneralLedger
      this.fixedAssetRegisterFormData.id = items.id
      this.isEditable = true
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Fixed Asset Register') : this.$t('View Fixed Asset Register')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.fixedAssetRegisterRules.validate().then(async success => {
        if (success) {
          const areYouSureText = this.$t('Are you sure you want to')
          const fixedText = this.$t('this Fixed Asset?')
          this.confirm(this.fixedAssetRegisterAction, { text: `${areYouSureText} ${this.buttonLabel} ${fixedText}` })
        }
      })
    },
    async fixedAssetRegisterAction() {
      try {
        this.hideModal('modal-lg')
        this.showSpinner = true
        this.isLoading = true
        const formData = {
          ...this.fixedAssetRegisterFormData,
          usefulLife: Number(this.fixedAssetRegisterFormData.usefulLife),
          originalBookValue: toKobo(Number(this.fixedAssetRegisterFormData.originalBookValue)),
          salvageValue: toKobo(Number(this.fixedAssetRegisterFormData.salvageValue)),
          monthlyDepreciationValue: toKobo(Number(this.fixedAssetRegisterFormData.monthlyDepreciationValue)),
          monthlyDepreciationRate: Number(this.fixedAssetRegisterFormData.monthlyDepreciationRate),
          annualDepreciationValue: toKobo(Number(this.fixedAssetRegisterFormData.annualDepreciationValue)),
          annualDepreciationRate: Number(this.fixedAssetRegisterFormData.annualDepreciationRate),
          accumulatedDepreciationValue: toKobo(Number(this.fixedAssetRegisterFormData.accumulatedDepreciationValue)),
          netAssetValue: toKobo(Number(this.fixedAssetRegisterFormData.netAssetValue)),
          initiatedBy: this.userDetails.email,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createFixedAssetRegister(formData)
          : await this.updateFixedAssetRegister(formData)
        const { status } = await response
        if (status === 200) {
          this.fetchFixedAssetRegister()
          const requestToText = this.$t('Your request to')
          const successText = this.$t('Fixed Asset was successful')
          this.alertSuccess({ text: `${requestToText} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const requestToText = this.$t('Your request to')
          const failedText = this.$t('Fixed Asset failed. Reason:')
          this.alertError({ text: `${requestToText} ${this.buttonLabel} ${failedText} ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
        this.showSpinner = false
      }
    },

    resetModal() {
      this.fixedAssetRegisterFormData.name = ''
      this.fixedAssetRegisterFormData.description = ''
      this.fixedAssetRegisterFormData.branch = ''
      this.fixedAssetRegisterFormData.branchName = ''
      this.fixedAssetRegisterFormData.category = ''
      this.fixedAssetRegisterFormData.location = ''
      this.fixedAssetRegisterFormData.serialNumber = ''
      this.fixedAssetRegisterFormData.registrationNumber = ''
      this.fixedAssetRegisterFormData.dateOfAcquisition = ''
      this.fixedAssetRegisterFormData.dateOfOperation = ''
      this.fixedAssetRegisterFormData.retirementDate = ''
      this.fixedAssetRegisterFormData.originalBookValue = ''
      this.fixedAssetRegisterFormData.salvageValue = ''
      this.fixedAssetRegisterFormData.usefulLife = ''
      this.fixedAssetRegisterFormData.monthlyDepreciationValue = ''
      this.fixedAssetRegisterFormData.monthlyDepreciationRate = ''
      this.fixedAssetRegisterFormData.annualDepreciationValue = ''
      this.fixedAssetRegisterFormData.annualDepreciationRate = ''
      this.fixedAssetRegisterFormData.accumulatedDepreciationValue = 0
      this.fixedAssetRegisterFormData.netAssetValue = ''
      this.fixedAssetRegisterFormData.paymentSourceGeneralLedger = ''
      this.fixedAssetRegisterFormData.fixedAssetGeneralLedger = ''
      this.fixedAssetRegisterFormData.accumulatedDepreciationGeneralLedger = ''
      this.fixedAssetRegisterFormData.depreciationExpenseGeneralLedger = ''
      this.fixedAssetRegisterFormData.fixedAssetDisposalGeneralLedger = ''
      this.isReadonly = false
      this.isLoading = false
      this.isEditable = false
      this.modalTitle = this.$t('Create Fixed Asset Register')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchFixedAssetRegister()
    },

    async getfixedAssetDepreciationExpenseGL() {
      try {
        const response = await this.getGeneralLedger({
          search: { accountCategoryCode: 50203000 },
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.depreciationExpenseGeneralLedgerOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async getfixedAssetAccumulatedDepreciationGL() {
      try {
        const response = await this.getGeneralLedger({
          search: { accountCategoryCode: 10801000 },
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.accumulatedDepreciationGeneralLedgerOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async getGLAccountsByAccountTypes() {
      try {
        const response = await this.getGLAccountsByAccountType({ accountType: 30, permission: this.permission })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.fixedAssetDisposalGeneralLedgerOptions = data.data.reduce((acc, curr) => [...acc, { label: `${curr.accountName} - ${curr.accountNumber}`, value: curr.accountNumber }], [])
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async getfixedAssetParentGL() {
      try {
        const response = await this.getfixedAssetParentGeneralLedger({
          search: '',
          page: 0,
          limit: 0,
          permission: this.permission,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.fixedAssetGeneralLedgerOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async getPaymentSourceGL() {
      try {
        const response = await this.getpaymentSourceGeneralLedger(this.permission)
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.paymentSourceGeneralLedgerOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }], [])
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async fetchFixedAssetRegister() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getFixedAssetRegister({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.fixedAssetRegisterList = data.data.map(item => ({
            ...item,
            monthlyDepreciationValue: toBalance(parseFloat(item.monthlyDepreciationValue).toFixed(2)),
            retirementDate: this.toDateWithYearFirst(moment(item.dateOfOperation, 'YYYY-MM-DD').add(item.usefulLife, 'months')),
          }))
          this.totalCount = data.recordCount
        } else {
          this.fixedAssetRegisterList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadFixedAssetRegisterReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: accountPermissions.VIEW_FIXED_ASSET,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadFixedAssetRegisterReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: accountPermissions.VIEW_FIXED_ASSET,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    computeDepreciation() {
      const {
        originalBookValue,
        salvageValue,
        usefulLife,
        accumulatedDepreciationValue,
      } = this.fixedAssetRegisterFormData
      this.fixedAssetRegisterFormData.monthlyDepreciationValue = parseFloat((originalBookValue - salvageValue) / usefulLife).toFixed(2)
      this.fixedAssetRegisterFormData.monthlyDepreciationRate = parseFloat((this.fixedAssetRegisterFormData.monthlyDepreciationValue / (originalBookValue - salvageValue)) * 100).toFixed(3)
      this.fixedAssetRegisterFormData.annualDepreciationValue = parseFloat(this.fixedAssetRegisterFormData.monthlyDepreciationValue * 12).toFixed(2)
      this.fixedAssetRegisterFormData.annualDepreciationRate = parseFloat(this.fixedAssetRegisterFormData.monthlyDepreciationRate * 12).toFixed(3)
      this.fixedAssetRegisterFormData.netAssetValue = accumulatedDepreciationValue >= 0 ? (originalBookValue - accumulatedDepreciationValue - salvageValue) : ''
    },
    async fixedAssetStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? 'DEACTIVATE_FIXED_ASSET' : 'ACTIVATE_FIXED_ASSET'

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateFixedAssetStatus({
            statusValues, actionType, routeName: 'FixedAssetRegister', permission,
          })
          if (response.status === 200) {
            this.fetchFixedAssetRegister()
            const requestToText = this.$t('Your request to')
            const regSuccessText = this.$t(' Fixed Asset Register was successful')
            this.alertSuccess({ text: `${requestToText} ${action} ${regSuccessText}` })
            this.showSpinner = false
          }
        } catch (error) {
          error.response.data.errors.forEach(responseError => {
            const requestToText = this.$t('Your request to')
            const failedText = this.$t('Fixed Asset Register failed. Reason:')
            this.alertError({ text: `${requestToText} ${action} ${failedText} ${responseError.errorMessage}` })
          })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSureText = this.$t('Are you sure you want to')
      const fixedRegText = this.$t('this Fixed Asset Register?')
      this.confirm(updateStatus, { title: '', text: `${areYouSureText} ${action} ${fixedRegText}` })
    },

    generateQRCode() {
      this.$root.$emit('bv::show::modal', 'modal-qrCode')
      this.qrCodePrintableValue = `i. Asset Name: ${this.fixedAssetRegisterFormData.name}
      \n ii. Asset Category: ${this.fixedAssetRegisterFormData.category}
      \n iii. Branch: ${this.fixedAssetRegisterFormData.branchName}
      \n iv. Registration/Tag Number: ${this.fixedAssetRegisterFormData.registrationNumber}
      \n v. Serial Number: ${this.fixedAssetRegisterFormData.serialNumber}
      \n vi. Date of Acquisition: ${this.formatDate(this.fixedAssetRegisterFormData.dateOfAcquisition)}
      \n vii. Retirement Date: ${this.formatDate(moment(this.fixedAssetRegisterFormData.dateOfOperation, 'YYYY-MM-DD').add(this.fixedAssetRegisterFormData.usefulLife, 'months'))}`
    },

    async handleLiquidateAsset() {
      const { id } = this.fixedAssetRegisterFormData
      const action = this.$t('Liquidate')
      const runAssetLiquidation = async () => {
        try {
          this.isBusy = true
          this.isLoading = true
          this.showSpinner = true
          const response = await this.liquidateAssetRegister({
            id,
            initiatedBy: this.userDetails.email,
            permission: accountPermissions.LIQUIDATE_FIXED_ASSET,
          })
          if (response.status === 200) {
            this.fetchFixedAssetRegister()
            this.alertSuccess({ text: `Your request to ${action} Fixed Asset Register was successful` })
            this.showSpinner = false
            this.isBusy = false
            this.isLoading = false
          }
        } catch (error) {
          error.response.data.errors.forEach(responseError => {
            const requestToText = this.$t('Your request to')
            const failedText = this.$t('Fixed Asset Register failed. Reason:')
            this.alertError({ text: `${requestToText} ${action} ${failedText} ${responseError.errorMessage}` })
          })
        } finally {
          this.showSpinner = false
          this.isBusy = false
          this.isLoading = false
        }
      }
      const areYouSureText = this.$t('Are you sure you want to')
      const fixedRegText = this.$t('this Fixed Asset Register?')
      this.confirm(runAssetLiquidation, { title: '', text: `${areYouSureText} ${action} ${fixedRegText}` })
    },

    async downloadQRCode() {
      const el = this.$refs.printcontent
      const fixedassetFile = this.$t('Fixed Asset')
      const filename = `${fixedassetFile} (${this.fixedAssetRegisterFormData.name}).png`
      const options = {
        type: 'dataURL',
      }
      const printCanvas = await html2canvas(el, options)
      const link = document.createElement('a')
      link.setAttribute('download', filename)
      link.setAttribute(
        'href',
        printCanvas
          .toDataURL('image/png')
          .replace('image/png', 'image/octet-stream'),
      )
      link.click()
    },

  },
}
</script>
